import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { PublicSiteHeader } from '@/components/PublicSiteHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PhotoThumb } from '@/components/PhotoThumb';
import { Loader2, MapPin, Calendar, ArrowLeft } from 'lucide-react';
import { rescuedAnimalsPublicApi } from '@/features/public/rescuedAnimalsPublicApi';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { toAbsoluteUrl } from '@/lib/url';

function daysRemaining(expiresAt?: string): number {
  if (!expiresAt) return 0;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

interface RescuedAnimalsPageProps {
  forcedShelter?: { id: number; name?: string; slug?: string };
}

export default function RescuedAnimalsPage({ forcedShelter }: RescuedAnimalsPageProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const shelterId = forcedShelter ? String(forcedShelter.id) : searchParams.get('shelterId');
  const { data: snapshot } = useLookupsSnapshot();

  const { data, isLoading } = useQuery({
    queryKey: ['public-rescued-animals', page, shelterId],
    queryFn: () => rescuedAnimalsPublicApi.search({ page, pageSize: 20, shelterId: shelterId || undefined }),
  });

  const speciesById = new Map<number, any>((snapshot?.species ?? []).map((s: any) => [s.id, s]));
  const sexById = new Map<number, any>((snapshot?.sexes ?? []).map((s: any) => [s.id, s]));
  const sizeById = new Map<number, any>((snapshot?.sizes ?? []).map((s: any) => [s.id, s]));
  const records: any[] = data?.data ?? [];
  const total: number = data?.total ?? 0;
  const pageSize: number = data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const detailPath = (id: number) => (forcedShelter?.slug ? `/${forcedShelter.slug}/encontrados/${id}` : `/encontrados/${id}`);

  return (
    <Layout showHeader={false} showFooter>
      <PublicSiteHeader />
      <div className="container mx-auto px-4 py-8">
        {forcedShelter?.slug && (
          <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
            <Link to={`/${forcedShelter.slug}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a {forcedShelter.name || 'la entidad'}
            </Link>
          </Button>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-semibold mb-2">
            {forcedShelter?.name
              ? `Animales en rescate, custodia y protección - ${forcedShelter.name}`
              : 'Animales en rescate, custodia y protección'}
          </h1>
          <p className="text-muted-foreground max-w-4xl">
            En esta sección encontrarás perros y gatos que han sido rescatados o puestos bajo custodia y
            protección {forcedShelter?.name ? `de ${forcedShelter.name}` : 'de la entidad correspondiente'} por
            encontrarse en situación de riesgo, vulnerabilidad, abandono o por otras circunstancias que requieren
            atención y protección.
            <br /><br />
            Si reconoces a uno de ellos como el tuyo, podrás iniciar el proceso correspondiente para verificar su
            identidad y solicitar su reclamación.
            <br /><br />
            Estos animales no están disponibles para adopción. Son diferentes de los animales publicados como
            extraviados, encontrados o disponibles para adopción.
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="p-4 animate-pulse h-64 bg-white/90" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <div className="text-center text-muted-foreground py-10">
            No hay animales encontrados publicados en este momento.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {records.map((r) => {
                const species = speciesById.get(r.speciesId);
                const sex = sexById.get(r.sexId);
                const size = sizeById.get(r.sizeId);
                const primaryPhoto = (r.photos ?? []).find((p: any) => p.isPrimary) ?? (r.photos ?? [])[0];
                const remaining = daysRemaining(r.expiresAt);
                return (
                  <Card
                    key={r.id}
                    className="group overflow-hidden rounded-xl transition-all duration-200 hover:shadow-lg border border-transparent hover:border-primary/20 bg-white cursor-pointer"
                    onClick={() => navigate(detailPath(r.id))}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="relative">
                      <div className="block w-full aspect-video bg-muted overflow-hidden">
                        {primaryPhoto ? (
                          <PhotoThumb
                            src={toAbsoluteUrl(primaryPhoto.url)}
                            alt={species ? toOptionLabel(species) : 'Animal'}
                            className="w-full h-full"
                            imgClassName="transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : null}
                      </div>
                      <span className="absolute right-2 top-2 z-20 rounded-full bg-white px-2.5 py-1 text-xs font-medium shadow">
                        {remaining} días
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">
                        {species ? toOptionLabel(species) : 'Animal'}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">{r.foundArea}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {sex && <span className="px-2 py-0.5 rounded-full border text-xs">{toOptionLabel(sex)}</span>}
                        {size && <span className="px-2 py-0.5 rounded-full border text-xs">{toOptionLabel(size)}</span>}
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> {String(r.intakeDate).slice(0, 10)}
                      </p>
                      {!shelterId && r.shelter?.name && (
                        <p className="mt-1 text-sm text-muted-foreground truncate flex items-center gap-2">
                          <MapPin className="h-4 w-4" /> {r.shelter.name}
                        </p>
                      )}
                      <div className="mt-4">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          asChild
                        >
                          <Link to={detailPath(r.id)} onClick={(e) => e.stopPropagation()}>
                            Ver detalles
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-8">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
